import { z } from "zod/v3";

// ===== Policy Approval Workflow =====
//
// Canonical state machine for `PolicyDocument.status`. Single source of
// truth — extend here when adding new states; mirror in
// `server/prisma/schema.prisma` comment on `PolicyDocument.status`.

export const PolicyApprovalStatusSchema = z.enum([
  "not_started",
  "draft",
  "generated",
  "pending_approval",
  "needs_changes",
  "approved",
]);

export type PolicyApprovalStatus = z.infer<typeof PolicyApprovalStatusSchema>;

// ===== Test detail linked policy claims =====

export const LinkedPolicyClaimDTOSchema = z.object({
  claimId: z.string(),
  occurrenceId: z.string(),
  claimText: z.string(),
  policyDocumentId: z.string(),
  policySlug: z.string(),
  policyTitle: z.string(),
  approvedVersion: z.number().int().positive(),
  docBlockId: z.string().nullable(),
  sectionPath: z.array(z.string()),
  /** Approved wording that needs re-verification; never silently counted as supported. */
  stale: z.boolean().optional(),
  /** False when the stored assertion cannot be traced to this approved body. */
  sourceResolved: z.boolean().optional(),
});

export type LinkedPolicyClaimDTO = z.infer<
  typeof LinkedPolicyClaimDTOSchema
>;

// ===== RFC-104: Policy Types =====

export const PolicyTypeSchema = z.enum([
  "INFORMATION_SECURITY",
  "ACCESS_CONTROL",
  "DATA_MANAGEMENT",
  "RISK_MANAGEMENT",
  "INCIDENT_RESPONSE",
  "BUSINESS_CONTINUITY",
  "SECURE_DEVELOPMENT",
  "OPERATIONS_SECURITY",
  "PHYSICAL_SECURITY",
  "CRYPTOGRAPHY",
  "CODE_OF_CONDUCT",
  "ACCEPTABLE_USE",
  "VENDOR_MANAGEMENT",
  "PRIVACY",
  "OTHER",
]);

export type PolicyType = z.infer<typeof PolicyTypeSchema>;

// ===== Policy Acknowledgment DTOs =====

export const PolicyAcknowledgmentDTOSchema = z.object({
  id: z.string(),
  policyDocumentId: z.string(),
  userId: z.string(),
  userName: z.string().nullable().optional(),
  userEmail: z.string().optional(),
  version: z.number(),
  acknowledgedAt: z.date(),
  ipAddress: z.string().nullable(),
  policyTitle: z.string().optional(),
});

export type PolicyAcknowledgmentDTO = z.infer<
  typeof PolicyAcknowledgmentDTOSchema
>;

export const CreatePolicyAcknowledgmentDTOSchema = z.object({
  policyDocumentId: z.string(),
  policyDocumentVersionId: z.string(),
  ipAddress: z.string().optional(),
});

export type CreatePolicyAcknowledgmentDTO = z.infer<
  typeof CreatePolicyAcknowledgmentDTOSchema
>;

// ===== Policy Summary (for list views) =====

export const PolicySummaryDTOSchema = z.object({
  policyDocumentId: z.string(),
  title: z.string(),
  status: z.string(),
  frameworkName: z.string().nullable().optional(),
  currentVersion: z.number(),
  totalUsers: z.number(),
  acknowledgedCount: z.number(),
  pendingCount: z.number(),
  acknowledgmentRate: z.number(),
  assignedEmployeeIds: z.array(z.string()).nullable().optional(),
});

export type PolicySummaryDTO = z.infer<typeof PolicySummaryDTOSchema>;

// ===== Policy approval readiness (#850) =====

export const ApprovalBlockerSchema = z.object({
  kind: z.enum(["hard_commitment", "contradicted_claim", "gap_finding"]),
  title: z.string(),
  detail: z.string().optional(),
  severity: z.enum(["critical", "high", "medium", "low"]).optional(),
  findingKey: z.string().nullable(),
  claimIds: z.array(z.string()).default([]),
});

export type ApprovalBlocker = z.infer<typeof ApprovalBlockerSchema>;

/** #1002: material vs non-material change classification. */
export const PolicyChangeTypeSchema = z.enum(["material", "non_material"]);

export type PolicyChangeType = z.infer<typeof PolicyChangeTypeSchema>;

/** #1002: AI "what changed since last approved" summary shown to the approver. */
export const PolicyChangeSummarySchema = z.object({
  text: z.string(),
  generatedAt: z.string().nullable(),
  /** Vera's suggested classification for the pending change (editor confirms). */
  suggestedChangeType: PolicyChangeTypeSchema.nullable(),
});

export type PolicyChangeSummary = z.infer<typeof PolicyChangeSummarySchema>;

export const ApprovalReadinessSchema = z.object({
  isBlocked: z.boolean(),
  /**
   * Tiered gate (claims review, 2026-08-09): blocking is reserved for
   * findings where signing would attest something the product knows is
   * false — contradicted claims and open gap findings. Unbacked
   * commitments and unlinked satisfied claims are actionable suggestions
   * (`commitmentSuggestions` / `claimsUnlinked`), not blockers: they are
   * frequently authored by our own templates or caused by our own linking
   * gaps, and blocking on them punished users with no resolution path.
   */
  hardCommitmentsUnresolved: z.array(ApprovalBlockerSchema),
  /** Non-blocking, actionable: unbacked template/prose commitments. */
  commitmentSuggestions: z.array(ApprovalBlockerSchema).default([]),
  claimsContradicted: z.array(ApprovalBlockerSchema),
  claimsUnverifiable: z.array(
    z.object({
      claimId: z.string().nullable(),
      draftClaimId: z.string().nullable(),
      claimText: z.string(),
      reason: z.string().optional(),
    }),
  ),
  claimsUnlinked: z.array(
    z.object({
      claimId: z.string(),
      claimText: z.string(),
      occurrenceId: z.string().nullable().optional(),
      dimensionKey: z.string().nullable().optional(),
      stance: z
        .enum(["SATISFIED", "GAP", "COMPENSATED", "ACCEPTED_RISK"])
        .optional(),
      /**
       * Historically "unproven satisfied claims block"; since the tiered
       * gate this flag only drives display grouping (needs-action vs
       * informational) — no unlinked claim blocks approval.
       */
      blocking: z.boolean().default(true),
      diverged: z.boolean().default(false),
    }),
  ),
  /** #1002: present when an AI change summary was generated on submit. */
  changeSummary: PolicyChangeSummarySchema.nullable().optional(),
});

export type ApprovalReadiness = z.infer<typeof ApprovalReadinessSchema>;

// ===== RFC-215: policy commitment rail =====

export const PolicyCommitmentRailRowSchema = z.object({
  kind: z.enum(["BLUEPRINT", "DOCUMENT"]),
  blueprintId: z.string(),
  question: z.string(),
  controlRef: z.string().nullable(),
  answer: z.object({
    answerRevisionId: z.string().nullable(),
    optionKey: z.string().nullable(),
    attestationAnswer: z
      .enum(["YES", "PARTIAL", "NO", "NA", "UNKNOWN"])
      .nullable(),
    rationale: z.string().nullable(),
    source: z.enum(["MANUAL", "IMPORTED", "SCANNER", "LEGACY_CES"]).nullable(),
    answeredAt: z.string().nullable(),
    answeredBy: z.string().nullable(),
  }),
  dimensions: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      required: z.boolean(),
      state: z.enum(["yes", "remediate", "compensate", "accept"]).nullable(),
      note: z.string().nullable(),
    }),
  ),
  projections: z.array(
    z.object({
      occurrenceId: z.string(),
      commitmentId: z.string(),
      dimensionKey: z.string().nullable(),
      origin: z.enum(["BLUEPRINT", "IMPORT", "MANUAL"]),
      stance: z.enum(["SATISFIED", "GAP", "COMPENSATED", "ACCEPTED_RISK"]),
      ownership: z.enum(["SYSTEM", "CUSTOMER"]),
      active: z.boolean(),
      diverged: z.boolean(),
      currentRevision: z
        .object({
          id: z.string(),
          text: z.string(),
          generatedBlockKey: z.string(),
          publishedVersionIds: z.array(z.string()),
        })
        .nullable(),
    }),
  ),
});

export type PolicyCommitmentRailRow = z.infer<
  typeof PolicyCommitmentRailRowSchema
>;

// ===== Bulk policy approval (#830) =====

export const PolicyApproveSkipReasonSchema = z.enum([
  "already_approved",
  "not_generated",
  "no_document",
  "forbidden",
  "not_found",
  "not_pending_approval",
  "not_approver",
  "blocked",
  "error",
]);

export type PolicyApproveSkipReason = z.infer<
  typeof PolicyApproveSkipReasonSchema
>;

export const BulkApprovePolicyApprovedSchema = z.object({
  policySlug: z.string(),
  title: z.string().optional(),
  version: z.number(),
  acknowledgedAt: z.string(),
});

export const BulkApprovePolicySkippedSchema = z.object({
  policySlug: z.string(),
  title: z.string().optional(),
  reason: PolicyApproveSkipReasonSchema,
  message: z.string().optional(),
});

export const BulkApprovePoliciesResultSchema = z.object({
  approved: z.array(BulkApprovePolicyApprovedSchema),
  skipped: z.array(BulkApprovePolicySkippedSchema),
  summary: z.object({
    requested: z.number(),
    approvedCount: z.number(),
    skippedCount: z.number(),
  }),
});

export type BulkApprovePoliciesResult = z.infer<
  typeof BulkApprovePoliciesResultSchema
>;
